/** Portal RBAC permission codes (single source of truth for Admin portal). */

export const PORTAL_PERMISSIONS = [
  'dashboard.view',
  'vehicles.view',
  'vehicles.create',
  'vehicles.edit',
  'vehicles.delete',
  'drivers.view',
  'drivers.create',
  'drivers.edit',
  'drivers.delete',
  'drivers.provision',
  'sos.view',
  'sos.handle',
  'jobs.view',
  'jobs.create',
  'jobs.edit',
  'jobs.delete',
  'daily_schedule.view',
  'daily_schedule.generate',
  'daily_schedule.publish',
  'trips.view',
  'trips.edit',
  'fleet_operators.view',
  'fleet_operators.create',
  'fleet_operators.edit',
  'fleet_operators.provision',
  'fleet_availability.view',
  'fleet_availability.edit',
  'organizations.view',
  'organizations.create',
  'organizations.edit',
  'organizations.provision',
  'operations.view',
  'reminders.view',
  'reminders.manage',
  'data_management.view',
  'data_management.import',
  'data_management.export',
  'settings.view',
  'settings.edit',
  'system_config.view',
  'system_config.edit',
  'owner_pending.view',
  'owner_pending.accept',
  'owner_pending.reject',
  'owner_schedule.view',
  'owner_organizations.view',
  'owner_organizations.create',
  'owner_organizations.edit',
  'owner_organizations.delete',
  'system.roles.view',
  'system.roles.manage',
  'system.users.view',
  'system.users.manage',
  'team.view',
  'team.manage',
  'team.promote_manager',
  'team.transfer_primary',
  'fleet_operators.members.view',
  'fleet_operators.members.disable',
  'fleet_profile.view',
  'fleet_profile.edit',
] as const;

export type PortalPermission = (typeof PORTAL_PERMISSIONS)[number];

/** Menu / route path → required view permission */
export const ROUTE_VIEW_PERMISSION: Record<string, PortalPermission> = {
  '/dashboard': 'dashboard.view',
  '/vehicles': 'vehicles.view',
  '/drivers': 'drivers.view',
  '/sos-events': 'sos.view',
  '/jobs': 'jobs.view',
  '/schedule-overview': 'jobs.view',
  '/daily-schedule': 'daily_schedule.view',
  '/trips': 'trips.view',
  '/fleet-operators': 'fleet_operators.view',
  '/fleet-availability': 'fleet_availability.view',
  '/organizations': 'organizations.view',
  '/vehicle-inspections': 'operations.view',
  '/operations': 'operations.view',
  '/data-management': 'data_management.view',
  '/settings': 'settings.view',
  '/system-config': 'system_config.view',
  '/reminder-settings': 'reminders.view',
  '/reminder-records': 'reminders.view',
  '/owner-pending': 'owner_pending.view',
  '/owner-schedule': 'owner_schedule.view',
  '/owner-organizations': 'owner_organizations.view',
  '/roles': 'system.roles.view',
  '/users': 'system.users.view',
  '/team': 'team.view',
  '/fleet-profile': 'fleet_profile.view',
};

export function hasPermission(
  codes: Iterable<string> | null | undefined,
  code: string
): boolean {
  if (!codes) return false;
  if (Array.isArray(codes)) return codes.includes(code);
  for (const c of codes) {
    if (c === code) return true;
  }
  return false;
}

export function viewPermissionForPath(pathname: string): PortalPermission | null {
  const entries = Object.entries(ROUTE_VIEW_PERMISSION).sort(
    (a, b) => b[0].length - a[0].length
  );
  for (const [prefix, perm] of entries) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
      return perm;
    }
  }
  return null;
}

export interface PortalAccessRole {
  id: string;
  code: string;
  name: string;
  is_system: boolean;
}

export interface PortalAccess {
  auth_uid: string;
  status: 'active' | 'disabled';
  display_name: string | null;
  role: PortalAccessRole;
  permissions: string[];
  fleet_operator_id?: string | null;
  is_primary_manager?: boolean;
}
